import { GetStaticProps } from 'next';
import Link from 'next/link';
import styled from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

const VocabularyEngagementOmanStudy = () => {
  return (
    <LandingPageLayout
      title="Research Study: AI Tools Improve Vocabulary and Engagement in Oman | Curipod"
      subTitle="A 2026 study found that fourth-grade students using Curipod showed significant improvement in English vocabulary and exhibited higher levels of engagement than those who did not use the tool."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>RESEARCH STUDY</HeroTag>
            <HeroTitle>
              AI-Generative Tools Improve Vocabulary and Engagement among Elementary
              School Students in Oman
            </HeroTitle>
            <HeroMeta>
              <MetaBadge>Elementary School (4th Grade)</MetaBadge>
              <MetaBadge>English Vocabulary</MetaBadge>
              <MetaBadge>2026 Study</MetaBadge>
            </HeroMeta>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    6.47<StatUnit>/10</StatUnit>
                  </StatNumber>
                  <StatLabel>Post-test &middot; Control Group</StatLabel>
                </StatItem>
                <ArrowContainer>&rarr;</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    8.00<StatUnit>/10</StatUnit>
                  </StatNumber>
                  <StatLabel>Post-test &middot; With Curipod</StatLabel>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* ABSTRACT */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Abstract</SectionTitle>
            <SectionText>
              Generative AI is a type of artificial intelligence that can enhance English
              vocabulary acquisition significantly. This study deploys a mixed methods
              approach investigating the effectiveness of AI-generative tools in improving
              the vocabulary and engagement of Omani 4th grade students in English
              Subject. Data was gathered using three instruments: (1) an English
              achievement test (pre-posttest), (2) an observation checklist and (3) a
              semi-structured interview. The pre-posttest measures the impact of using the
              Curipod tool on the students&apos; English vocabulary. The sample includes
              62 fourth-grade students at Al-Abrar School for Basic Education (grades
              1-4), 30 students in the experimental group and 32 in the control group. The
              observation checklist inspects the 62 students across both groups while the
              semi-structured interview was administered to two teachers responsible for
              the groups. The findings indicate that students exposed to the Curipod tool
              showed significant improvement in their post-test and that they exhibited
              higher levels of engagement than those who did not use the tool. Therefore,
              the study recommends that educators and policy makers integrate AI-driven
              tools such as Curipod into Oman&apos;s English education to enhance
              vocabulary acquisition and academic performance. They should also provide
              teachers with structured training to assist them using these technologies
              effectively.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* INTRODUCTION */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Introduction</SectionTitle>
            <SectionText>
              Due to the rapid advancement of technology and the impact of the COVID-19
              dilemma, educational associations have hastened to adjust swiftly to the
              evolving situations. Accordingly, teachers start using platforms like Moodle
              and Microsoft Teams to teach students directly and provide supplementary
              tools for independent learning.
            </SectionText>
            <SectionText>
              Currently, the integration of AI in education renovates e-learning by
              personalizing experiences, automating administrative tasks and offering
              adaptive learning pathways for students. Considering the challenges
              mentioned above and the critical role of AI in English vocabulary teaching,
              this research intends to take a step further and assess the impact of
              Curipod on teaching vocabulary and engagement of Omani 4th grade students in
              English classes.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* FINDINGS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Findings</SectionTitle>
            <SectionText>
              The study&apos;s mixed-method approach provides comprehensive results
              concerning the effect of the AI-generative tool (Curipod) on fourth-grade
              students&apos; English vocabulary acquisition and their classroom
              engagement.
            </SectionText>
            <SectionText>
              Regarding the first research question, the results indicate that students
              who applied the Curipod tool during the learning process improved their
              English vocabulary more than those who did not. There was no significant
              difference in mean pretest scores between the control group (M=3.06;
              SD=1.81) and experimental group (M=2.57; SD=1.57) before the intervention
              (t(60)=1.15, p&gt;.05). On the other hand, there was a statistically
              significant difference between the post-test mean scores of the experimental
              group (M=8.00; SD=1.76) and the control group (M=6.47; SD=1.87),
              (t(60)=3.32, p=0.002&lt;.05) in favor of the experimental group.
            </SectionText>
            <SectionText>
              The findings for the second research question reveal that students who
              utilized the Curipod tool showed a high degree of engagement in both domains
              (cognitive and emotional) as highlighted by the observation checklist and
              semi-structured interviews. Students in the experimental group exhibited
              higher levels of interest and motivation and perceived usefulness compared
              to the control group.
            </SectionText>

            <BlockQuote>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                I&apos;ve noticed a significant increase in students&apos; active
                participation, enthusiasm, and motivation when interacting with the
                platform&hellip;
              </QuoteText>
              <QuoteAttrib>&mdash; Teacher of the Experimental Group</QuoteAttrib>
            </BlockQuote>

            <SectionText style={{ marginTop: 32 }}>
              Furthermore, sense of connection and belonging to the learning community
              play a crucial role in shaping students&apos; engagement. Students who had
              access to Curipod worked together, shared ideas and supported each other in
              solving challenges. It was also noticed that the content and activities
              developed using the Curipod tool offered the students opportunities to apply
              critical thinking skills and cooperate with peers during task completion.
            </SectionText>

            <BlockQuote>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                When engaging in collaborative content and activities, students seek help
                from both the instructor and their peers when they can&apos;t understand a
                concept taught in the class&hellip;
              </QuoteText>
              <QuoteAttrib>&mdash; Teacher of the Experimental Group</QuoteAttrib>
            </BlockQuote>

            <SectionText style={{ marginTop: 32 }}>
              In contrast, students in the control group could not communicate with their
              peers and the teacher as effectively.
            </SectionText>

            <BlockQuote>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                We get bored with this type of activities, and it does not stimulate my
                motivation to learn. We want the class to be more enjoyable...
              </QuoteText>
              <QuoteAttrib>&mdash; Student from the Control Group</QuoteAttrib>
            </BlockQuote>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* CONCLUSION */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Conclusion</SectionTitle>
            <SectionText>
              This study intends to evaluate the effectiveness of an AI-generative tool
              (Curipod) in improving vocabulary acquisition and engagement of Omani 4th
              grade students in English classes. The findings of this study demonstrate
              that students in the experimental group, who utilized the Curipod tool,
              showed a significant development in English vocabulary and displayed a
              higher degree of engagement in the emotional and cognitive domains compared
              to students in the control group. Consequently, the study concludes that
              AI-generative tools such as Curipod not only improve vocabulary acquisition
              but also create a more interactive and engaged environment.
            </SectionText>
            <SectionText>
              Overall, integrating AI-generative tools, such as Curipod, holds promising
              potential for enhancing English language learning outcomes and promoting
              students&apos; engagement among fourth-grade Omani students. Future research
              can benefit from this study&apos;s results as a guide for further
              exploration of the effectiveness of AI tools in teaching languages.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* SUGGESTIONS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Suggestions</SectionTitle>
            <SuggestionList>
              <SuggestionItem>
                Educators and policymakers should explore incorporating AI-driven tools
                such as Curipod into English language education throughout schools in
                Oman. This incorporation can enhance students&apos; vocabulary acquisition
                and boost overall academic accomplishments.
              </SuggestionItem>
              <SuggestionItem>
                Teachers should be provided with training and professional development
                programs to proficiently integrate AI tools into their teaching
                approaches.
              </SuggestionItem>
              <SuggestionItem>
                Curriculum developers should investigate methods to integrate AI-powered
                educational technologies into the curriculum offering students interactive
                and personalized learning experiences.
              </SuggestionItem>
              <SuggestionItem>
                Researchers should conduct longitudinal studies to gauge the long-term
                effects of incorporating Curipod and similar technologies on
                students&apos; language proficiency and academic achievements.
              </SuggestionItem>
            </SuggestionList>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* SOURCE & CTA */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SourceCard>
              <SourceLabel>SOURCE</SourceLabel>
              <SourceText>
                AL Naabi, F., &amp; Al Abri, M. (2026). Exploring the Effectiveness of
                AI-Generative Tools in Improving Vocabulary and Engagement among
                Elementary School Students in Oman.{' '}
                <em>The Turkish Online Journal of Educational Technology (TOJET)</em>,
                25(1), 114&ndash;126.
              </SourceText>
              <a
                href="https://www.tojet.net/volumes/v25i1.pdf#page=106"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SourceLink>View full paper &rarr;</SourceLink>
              </a>
            </SourceCard>

            <GetStartedWrapper>
              <Link href="/signup" passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default VocabularyEngagementOmanStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: ${theme.colors.paleBeige};
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 52px;
  color: ${theme.colors.black};
  margin: 0 0 20px;
  line-height: 1.15;
  max-width: 800px;

  @media ${MOBILE} {
    font-size: 36px;
  }

  @media (max-width: 320px) {
    font-size: 28px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const StatBannerWrapper = styled.div`
  background: ${theme.colors.black};
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0;
  padding: 0 20px;

  @media ${MOBILE} {
    gap: 20px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 16px 24px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${({ $highlight }) => ($highlight ? theme.colors.orange : theme.colors.white)};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatUnit = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 26px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  a {
    color: ${theme.colors.orange};
    text-decoration: underline;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── QUOTES ────────────────────────────────────────────────────────────────── */

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── SUGGESTIONS ───────────────────────────────────────────────────────────── */

const SuggestionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SuggestionItem = styled.li`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  padding: 16px 0 16px 28px;
  position: relative;
  border-left: 3px solid ${theme.colors.orange};
  margin-left: 12px;
  margin-bottom: 8px;

  &::before {
    content: '';
    position: absolute;
    left: -7px;
    top: 22px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${theme.colors.orange};
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

/* ─── SOURCE & CTA ──────────────────────────────────────────────────────────── */

const SourceCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 12px;
  padding: 24px 32px;
  box-shadow: ${theme.effects.defaultShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 20px;
  }
`;

const SourceLabel = styled.div`
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  margin-bottom: 8px;
`;

const SourceText = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin: 0 0 8px;
`;

const SourceLink = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orange};
  &:hover {
    text-decoration: underline;
  }
`;

const GetStartedWrapper = styled.div`
  margin-top: 8px;
`;
